import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnDestroy,
  Output,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Subject, takeUntil } from 'rxjs';
import { Profile, ICheckApiKeyResponse } from 'src/app/models/profile.model';
import { ResponseCustomType } from 'src/app/types/response/response.type';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { ProfileService } from 'src/app/services/profile.service';
import { ModeSwitchService } from 'src/app/services/mode-switch.service';
import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { BypassInterceptorContext } from 'src/app/interceptors/http.interceptor';
import { environment } from 'src/environments/environment';
import { LiveChatService } from 'src/app/services/live-chat.service';

@Component({
  standalone: false,
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
})
export class NavbarComponent implements OnInit, OnDestroy {
  @Input() active: boolean = true;
  @Output() collapseToogle = new EventEmitter();
  @Output() openModalAlertSwitchEvent = new EventEmitter<boolean>();
  @Output() openNotifDrawerhEvent = new EventEmitter<boolean>();

  private destroy$ = new Subject<void>();
  detailProfile: Profile = {} as Profile;
  profileMode: boolean = false;
  currentMode: string = '';
  activeUrl: string = '';
  openModalAlertNumber: boolean = false;
  openModalAlertSwitch: boolean = false;
  isNotifDrawerOpen: boolean = false;
  totalNotif: number = 0;
  iconsPackage: { starter: string, pro: string, enterprise: string } = {
    starter: 'https://storage.googleapis.com/komerce/rajaongkir/Label-starter.svg',
    pro: 'https://storage.googleapis.com/komerce/rajaongkir/Label-pro.svg',
    enterprise: 'https://storage.googleapis.com/komerce/rajaongkir/Label-enterprise.svg'
  }

  constructor(
    private storageService: LocalStorageService,
    private router: Router,
    private profileService: ProfileService,
    private message: NzMessageService,
    private modeSwitchService: ModeSwitchService,
    private httpClient: HttpClient,
    private liveChatService: LiveChatService,
  ) {
    this.router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe((val) => {
        if (val instanceof NavigationEnd) {
          this.activeUrl = val.url || '';
        }
      });
  }

  ngOnInit(): void {
    this.profileService.profileData$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          if (data) {
            localStorage.setItem('pin_status', JSON.stringify(data.pin_status));
            this.detailProfile = data;
            this.openModalAlertNumber = this.detailProfile.phone_number === '';
            if (data.email) {
              this.liveChatService.login(data.email);
            }
          }
        },
        error: (error) => console.error('Error fetching profile data:', error),
      });

    this.modeSwitchService.mode$
      .pipe(takeUntil(this.destroy$))
      .subscribe((config) => {
        const previousMode = this.currentMode;
        this.currentMode = config.mode;
        if (previousMode !== this.currentMode) {
          this.profileService.getProfile();
        }
      });

    const savedMode = this.modeSwitchService.getVerifiedMode();
    this.currentMode = savedMode;
    this.profileMode = savedMode === 'production';
    this.modeSwitchService.switchMode(savedMode);
  }

  onTotalNotifChanged(newTotal: number) {
    this.totalNotif = newTotal;
  }

  closeSwitchMode() {
    this.openModalAlertSwitch = false;
    this.currentMode = this.modeSwitchService.getVerifiedMode();
    this.profileMode = false;
  }

  toggleEnvironment() {
    const currentMode = this.modeSwitchService.getCurrentMode().mode;
    const newMode = currentMode === 'sandbox' ? 'production' : 'sandbox';

    const performSwitch = () => {
      this.message.success(`Your account has successfully switched to ${this.profileMode ? 'Live' : 'Sandbox'} Mode`);

      this.modeSwitchService.switchMode(newMode);

      const redirectPaths = ['/order-data', '/finance/balance', '/finance/commission', '/ticketing'];
      const matchedPath = redirectPaths.find(path => this.activeUrl.startsWith(path));

      if (matchedPath) {
        this.router.navigate([matchedPath]);
      } else if (this.activeUrl.startsWith('/subscriptions')) {
        this.router.navigate(['/subscriptions']);
      }

      setTimeout(() => window.location.reload(), 500);
    };

    if (newMode === 'sandbox') {
      performSwitch();
      return;
    }

    const baseUrl = environment.SWITCH_URL;
    const apiUrl = `${baseUrl}/user/api/v1/user/check-api-key`;

    const headers = new HttpHeaders({
      Accept: 'application/json',
      Authorization: `Bearer ${localStorage.getItem('auth-token')}`,
    });

    const context = new HttpContext().set(BypassInterceptorContext, true);

    this.httpClient.get<ResponseCustomType<ICheckApiKeyResponse>>(apiUrl, { headers, context }).subscribe({
      next: (res) => {
        if (
          res.data?.is_qrisly_complete_document === true ||
          res.data?.is_payment_complete_document === true ||
          res.data?.is_shipping_complete_document === true
        ) {
          performSwitch();
        } else {
          this.modeSwitchService.switchMode(currentMode);
          this.message.warning('Oops! Live mode isn’t available. Contact support for Live mode access');
          this.profileMode = false;
          this.openModalAlertSwitch = true;
        }
      },
      error: (error) => {
        console.error('Switch mode error:', error);
        this.message.error('Failed to switch modes. Please try again later.');
      },
    });
  }

  collapseClick(event: Event): void {
    event.stopPropagation();
    this.active = !this.active;
    this.collapseToogle.emit(this.active);
  }

  logout(): void {
    this.storageService.signOut();
    (window as any).CRISP_TOKEN_ID = '';
    (window as any).$crisp?.push(['do', 'session:reset']);
    this.liveChatService.logout();
    window.location.reload();
  }

  openNotifDrawer(): void {
    this.isNotifDrawerOpen = !this.isNotifDrawerOpen;
    this.openNotifDrawerhEvent.emit(this.isNotifDrawerOpen);
  }

  closeNotifDrawer(): void {
    this.isNotifDrawerOpen = false;
    this.openNotifDrawerhEvent.emit(this.isNotifDrawerOpen);
  }

  getPackageIcon(): string {
    if (this.detailProfile.rajaongkir_level === 1) {
      return this.iconsPackage.starter;
    } else if (this.detailProfile.rajaongkir_level === 2) {
      return this.iconsPackage.pro;
    } else if (this.detailProfile.rajaongkir_level === 3) {
      return this.iconsPackage.enterprise;
    }
    return this.iconsPackage.starter;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
